import React, { useState, forwardRef, useImperativeHandle } from 'react';
import { DatePicker, Form, Input, Checkbox } from 'antd';
import ModalWithAlert from './modal';
import Button from '../button';
import Text from '../text';
import PropTypes from 'prop-types';
import moment from 'moment';

const EditWork = forwardRef((props, ref) => {
    const [form] = Form.useForm();
    const initialFields = [
        { name: ['company_name'], value: null },
        { name: ['team_name'], value: null },
        { name: ['job_title'], value: null },
        { name: ['start_date'], value: null },
        { name: ['end_date'], value: null },
        { name: ['present'], value: false },
        { name: ['location'], value: null },
        { name: ['details'], value: null },
    ];

    // States
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [modalAlerts, setModalAlerts] = useState([]);
    const [modalTitle, setModalTitle] = useState(null);
    const [fields, setFields] = useState([]);
    const [isEdit, setIsEdit] = useState(false);
    const [isPresent, setIsPresent] = useState(false);
    const [workIdCounter, setWorkIdCounter] = useState(0);

    // Actions
    useImperativeHandle(ref, () => ({
        showModal(data) {
            if (data) {
                setIsPresent(data['end_date'] == 'Present');
                setModalTitle('Edit Work Experience');
                setIsEdit(true);
                setFields([
                    { name: ['company_name'], value: data['company_name'] },
                    { name: ['team_name'], value: data['team_name'] },
                    { name: ['job_title'], value: data['job_title'] },
                    { name: ['start_date'], value: data['start_date'] ? moment(data['start_date']) : null }, // sets date to null if field is empty
                    { name: ['end_date'], value: data['end_date'] !== 'Present' ? moment(data['end_date']) : null },
                    { name: ['present'], value: data['end_date'] == 'Present' },
                    { name: ['location'], value: data['location'] },
                    { name: ['details'], value: data['details'] },
                ]);
            } else {
                setModalTitle('Add Work Experience');
                setIsEdit(false);
                setFields(initialFields);
            }
            setIsModalVisible(true);
        }
    }))

    const handleModalClose = () => {
        setIsModalVisible(false);
        setFields(initialFields);
        setModalAlerts([]);
    }

    const handleModalSubmit = () => {
        form.validateFields().then(values => {
            let endDate = null;
            if (isPresent) {
                endDate = 'Present';
            } else if (values.end_date) {
                endDate = values.end_date.format('MMM YYYY');
            }

            const workData = {
                id: workIdCounter, // assigns a new counter
                company_name: values.company_name,
                team_name: values.team_name,
                job_title: values.job_title,
                start_date: values.start_date ? values.start_date.format('MMM YYYY') : null,
                end_date: endDate,
                location: values.location,
                details: values.details
            };
            if (isEdit) {
                props.editWork(workData);
                setWorkIdCounter(workIdCounter + 1); // since each time you edit, that work's id changes to idCounter
            } else {
                props.addWork(workData);
                setWorkIdCounter(workIdCounter + 1);
            }

            handleModalClose();
        }).catch(() => {
            setModalAlerts([
                ...modalAlerts,
                {
                    type: 'error',
                    content: <Text id='modal::edit-work-experience::message::validate-error' />
                }
            ]);
        });
    }

    return (
        <ModalWithAlert
            title={modalTitle}
            visible={isModalVisible}
            onCancel={handleModalClose}
            alerts={modalAlerts}
            footer={[]}
        >
            <div className='flex-column text-gray mt-3'>
                <Form
                    form={form}
                    fields={fields}
                    onFieldsChange={(_, newFields) => {
                        setFields(newFields);
                    }}
                    layout='vertical'>
                    <Form.Item
                        name='company_name'
                        label='Company Name'
                        rules={[{ required: true, message: 'Company name required' }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name='team_name'
                        label='Team Name'>
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name='job_title'
                        label='Job Title'
                        rules={[{ required: true, message: 'Job title required' }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name='start_date'
                        label='Start Date'
                        rules={[{ required: true, message: 'Start date required' }]}>
                        <DatePicker className='w-100' format="MM/YYYY" picker="month" />
                    </Form.Item>
                    <Form.Item
                        name='end_date'
                        label='End Date'
                        className='mb-0'
                        rules={[
                            {
                                validator: (_, value) => {
                                    if (isPresent || value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('End Date required'));
                                },
                            },
                        ]}>
                        <DatePicker className='w-100' format="MM/YYYY" picker="month" disabled={isPresent} />
                    </Form.Item>
                    <Form.Item name='present' valuePropName="checked">
                        <Checkbox onChange={(e) => { setIsPresent(e.target.checked); }}>
                            <Text id='modal::edit-work-experience::text::present' />
                        </Checkbox>
                    </Form.Item>
                    <Form.Item name='location' label='Location'>
                        <Input />
                    </Form.Item>
                    <Form.Item name='details' label='Details'>
                        <Input.TextArea rows={5} />
                    </Form.Item>
                </Form>
                <div className='d-flex justify-content-center'>
                    <Button key='submit' onClick={handleModalSubmit} className='mt-4 col-4'>
                        <Text id='modal::edit-interview::button::save' />
                    </Button>
                </div>
            </div>
        </ModalWithAlert>
    );
})

export default EditWork;
EditWork.propTypes = {
    addWork: PropTypes.func,
    editWork: PropTypes.func
}